/* ─────────────────────────────────────────
   Variant D: オープニング演出（PKSHA風）
   ① SVGライン描画 → ② 文字1文字ずつ出現
   → ③ 見出し行ごとスライドアップ
   → ④ ロゴ画像フェードイン → ⑤ CTAボタン
───────────────────────────────────────── */
const LABEL_CHARS = 'Multi Field Developer'.split('')

/** 行ごとの見出しデータ */
const H1_LINES = [
  { text: '様々な技術分野に', colored: false },
  { text: '対応する', colored: false },
  { text: 'テクノロジー', colored: true },
  { text: 'カンパニー', colored: true }
]

/**
 * 各文字のアニメーション delay をモジュールロード時にランダム生成。
 * 行ごとの base delay に 0〜0.7s のランダム幅を加えることで、
 * 「ある瞬間に一部の文字だけブラーから復帰中」に見える。
 */
const H1_CHAR_DELAYS: number[][] = H1_LINES.map(({ text }, lineIdx) => {
  const base = 1.5 + lineIdx * 0.2
  return text.split('').map(() => base + Math.random() * 0.7)
})

export function Hero() {
  return (
    <section
      id="top"
      className="relative min-h-screen overflow-hidden bg-white"
    >
      {/* ─── 背景グラデーション ─── */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-slate-100" />

      {/* ─── SVGライン装飾（① 最初に描画） ─── */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 1440 900"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {/*
          4本の流れる曲線
          - lineDrawIn : stroke-dashoffset を操作（描画）
          - waveFloat* : transform translateY を操作（波打ち）
          別プロパティなので同一要素に並列指定可能
        */}
        <path
          pathLength="1"
          fill="none"
          stroke="#c0d4e0"
          strokeWidth="1"
          d="M-60,820 C200,700 450,640 750,600 S1100,580 1350,480 S1500,360 1560,280"
          style={{
            strokeDasharray: 1,
            strokeDashoffset: 1,
            animation: [
              'lineDrawIn 1.1s cubic-bezier(0.4,0,0.2,1) 0.05s forwards',
              'waveFloat1 7s ease-in-out 1.2s infinite'
            ].join(', ')
          }}
        />
        <path
          pathLength="1"
          fill="none"
          stroke="#b0c8d8"
          strokeWidth="1"
          d="M-60,760 C180,650 420,590 700,550 S1050,530 1300,440 S1460,330 1560,240"
          style={{
            strokeDasharray: 1,
            strokeDashoffset: 1,
            animation: [
              'lineDrawIn 1.1s cubic-bezier(0.4,0,0.2,1) 0.18s forwards',
              'waveFloat2 9s ease-in-out 1.4s infinite'
            ].join(', ')
          }}
        />
        <path
          pathLength="1"
          fill="none"
          stroke="#ccdae8"
          strokeWidth="0.8"
          d="M-60,880 C220,790 500,740 800,700 S1100,680 1320,600"
          style={{
            strokeDasharray: 1,
            strokeDashoffset: 1,
            animation: [
              'lineDrawIn 0.95s cubic-bezier(0.4,0,0.2,1) 0.28s forwards',
              'waveFloat3 8s ease-in-out 1.3s infinite'
            ].join(', ')
          }}
        />
        <path
          pathLength="1"
          fill="none"
          stroke="#d8e6f0"
          strokeWidth="0.6"
          d="M-60,700 C150,600 340,550 560,520 S820,500 980,440"
          style={{
            strokeDasharray: 1,
            strokeDashoffset: 1,
            animation: [
              'lineDrawIn 0.85s cubic-bezier(0.4,0,0.2,1) 0.12s forwards',
              'waveFloat2 6s ease-in-out 1.0s infinite'
            ].join(', ')
          }}
        />
      </svg>

      {/* ─── メインコンテンツ ─── */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 min-h-screen grid grid-cols-1 lg:grid-cols-2 gap-0 items-center pt-20 pb-12">
        {/* テキスト左 */}
        <div className="flex flex-col justify-center">
          {/* ② "Multi Field Developer" — 文字1文字ずつスライドアップ */}
          <div className="flex items-center gap-3 mb-6">
            <span
              className="w-1 h-6 bg-brand-cyan rounded-full"
              style={{ animation: 'charSlideUp 0.4s ease-out 0.9s both' }}
            />
            <span className="flex text-brand-cyan text-sm font-semibold tracking-widest uppercase overflow-hidden">
              {LABEL_CHARS.map((ch, i) => (
                <span
                  key={i}
                  className="inline-block"
                  style={{
                    animation: `charSlideUp 0.35s ease-out ${0.9 + i * 0.028}s both`
                  }}
                >
                  {ch === ' ' ? '\u00A0' : ch}
                </span>
              ))}
            </span>
          </div>

          {/* ③ 見出し — 文字ごとにランダム方向からアニメーション */}
          <h1 className="mb-0">
            {H1_LINES.map(({ text, colored }, lineIdx) => (
              <div key={lineIdx} className="flex flex-wrap">
                {text.split('').map((ch, charIdx) => (
                  <span
                    key={charIdx}
                    className={`inline-block text-4xl md:text-5xl lg:text-6xl font-bold leading-tight ${
                      colored ? 'text-brand-blue' : 'text-brand-navy'
                    }`}
                    style={{
                      animation: `charBlurIn 0.65s ease-out ${H1_CHAR_DELAYS[lineIdx][charIdx].toFixed(3)}s both`
                    }}
                  >
                    {ch}
                  </span>
                ))}
              </div>
            ))}
          </h1>

          <div
            className="w-16 h-1 bg-brand-cyan rounded-full mt-6 mb-6"
            style={{ animation: 'fadeUp 0.5s ease-out 2.2s both' }}
          />

          <p
            className="text-brand-navy/60 text-base md:text-lg leading-relaxed mb-10 max-w-md"
            style={{ animation: 'fadeUp 0.6s ease-out 2.4s both' }}
          >
            スマートフォンアプリからWEBシステム、Fintech、VR/ARまで。
            AIを駆使した開発手法で、最先端のソリューションを提供します。
          </p>

          {/* ⑤ CTAボタン */}
          <div
            className="flex flex-col sm:flex-row gap-4"
            style={{ animation: 'fadeUp 0.6s ease-out 2.6s both' }}
          >
            <a href="#contact" className="btn-primary">
              お問い合わせ
            </a>
            <a
              href="#features"
              className="btn-outline border-brand-navy/30 text-brand-navy/70 hover:bg-brand-navy/5 hover:border-brand-navy/60"
            >
              当社について
            </a>
          </div>
        </div>

        {/* ④ ロゴ画像右 — テキスト後にフェードイン */}
        <div
          className="relative flex flex-col items-center justify-center"
          style={{ animation: 'fadeUp 0.9s ease-out 1.9s both' }}
        >
          <div className="absolute w-80 h-80 bg-brand-cyan/10 rounded-full blur-3xl" />
          <img
            src="/images/logo.png"
            alt="J-Frontier"
            className="relative w-full max-w-3xl h-auto drop-shadow-2xl rounded-2xl"
          />
          <div className="relative mt-4 text-center">
            <p className="text-xs font-light tracking-[0.3em] text-brand-navy/50 uppercase">
              J-Frontier Co., Ltd.
            </p>
            <p className="animate-gradient-text text-base font-bold tracking-[0.2em] mt-0.5">
              株式会社ジェイフロンティア
            </p>
          </div>
        </div>
      </div>

      {/* スクロールインジケーター */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        style={{ animation: 'fadeUp 0.6s ease-out 2.9s both' }}
      >
        <span className="text-brand-navy/30 text-xs tracking-widest">
          SCROLL
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-brand-cyan/60 to-transparent animate-pulse" />
      </div>
    </section>
  )
}
